import { useMemo, useState } from "react";
import {
  Button,
  Input,
  Skeleton,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@flint/ui";
import { paiseToInrExact } from "../../../lib/format.ts";
import { perPiecePaise, rupeesToPaise, withGstPaise } from "../price-math.ts";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";
import { variantKey, type VariantAttribute } from "@flint/domain/commerce/store/variant-matrix";
import { packSaveAction } from "@flint/domain/commerce/store/pack-save-action";
import { bundleFromVariantSelection } from "./variant-form.ts";
import {
  type PricingEntry,
  compositionPairs,
  entryPiecesOnHand,
  serialiseComposition,
} from "./size-ratio.ts";
import {
  BundleCompositionBuilder,
  ColorSpecPicker,
  type ColorMode,
  type CompositionRow,
} from "./bundle-builder.tsx";

export interface PricingEntryDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Every entry on the product — for the already-priced check. */
  siblings: readonly PricingEntry[];
  /** Index being edited; omit to add. Excluded from its own checks. */
  editIndex?: number | undefined;
  /** Raw GST field value, in percent — for the incl-GST line. */
  taxPct: string;
  /**
   * The product's variant axes (#234). The Size axis feeds the Bundle
   * Builder's steppers; the Color axis feeds the swatches. A usable Size axis
   * is now REQUIRED to add a price (#247) — without one there is no way to
   * compose a bundle, and the dialog says so instead of offering a picker.
   */
  variantAttributes?: readonly VariantAttribute[] | undefined;
  /**
   * The product's generated variant rows. When present, the builder lists
   * them (one stepper per combination) instead of bare sizes, and both the
   * pack composition and the colour spec derive from the picks. Structural
   * subset of `VariantDraft` — the builder needs only combination + SKU.
   */
  productVariants?: readonly { attributeValues: Record<string, string>; sku: string }[] | undefined;
  /**
   * Coverage rule at save time (ADR-0065 companion): called with this entry's
   * pieces on hand (composition × quantity) and the index it replaces; a
   * returned message blocks the save and renders inline. Absent on the edit
   * form (no opening stock there).
   */
  coverageGuard?: ((candidatePiecesOnHand: number, editIndex?: number) => string | null) | undefined;
  onSave: (entry: PricingEntry) => void;
}

function pctToBps(pct: string): number {
  const n = Number.parseFloat(pct);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : 0;
}

/**
 * The axis whose name means "size"/"colour", matched case-insensitively with
 * singular/plural tolerance ("Size", "sizes", "Colours" all resolve). The
 * names are a recognition heuristic, not a constraint — an axis named
 * anything else still works everywhere except this dialog's builder, which
 * simply can't know which axis is the size curve. Deliberately no
 * first-axis fallback: guessing that "Material" is the size axis would build
 * a nonsense scale.
 */
function findAxis(
  axes: readonly VariantAttribute[] | undefined,
  names: readonly string[],
): { name: string; values: string[] } | undefined {
  const axis = axes?.find((a) => {
    const n = a.name.trim().toLowerCase();
    return names.includes(n) || names.includes(n.replace(/s$/, ""));
  });
  if (!axis) return undefined;
  const values = axis.values.map((v) => v.trim()).filter((v) => v.length > 0);
  return values.length > 0 ? { name: axis.name.trim(), values } : undefined;
}

/** Scale labels cap at 8 chars (`normalizeScaleLabels`); sizes must fit. */
const LABEL_MAX = 8;

/** A scale past this many labels is a data-entry mistake, not a size curve. */
const SIZES_MAX = 24;

/**
 * Add or edit **one way to buy** the product — a library pack at a price
 * (spec 13, R4). The Bundle Builder is the ONLY way to create one (#247): the
 * "Pick from library" tab it used to sit beside is gone, so the dialog opens
 * straight into per-size steppers over the product's Size axis, template
 * chips and a live preview. Saving creates a REAL library pack
 * (`ensureScale` → `storePacks.create`) and prices it here — the inline path
 * spec 14 S-PK4 specified but never shipped. Nothing is ad-hoc: every bundle
 * lands in the shared library — which #248 then unlinked from the store rail,
 * so `/store/admin/packs` is URL-only now (route still resolves; review
 * 2026-11-10). Packs remain real and order-referenced; the library just isn't
 * a place you visit, and never was a source you price *from* after #247.
 *
 * The cost of dropping the picker: a product with no usable Size axis has
 * nothing to compose, so adding a price is refused with the reason named
 * rather than silently falling back.
 *
 * **Editing is the same builder, prefilled** — one form, not two. It composes
 * over the product's variants when the entry can be replayed at that grain,
 * else its sizes, else (edit only) the pack's own labels recovered from the
 * entry's `sizeRatio`. That last fallback is what keeps a legacy entry, or one
 * whose axes have since changed, editable in the same form instead of
 * dead-ending — and it needs no library round-trip, because R2 makes the
 * serialised ratio the full label set.
 *
 * Colour is an entry overlay (`colorSpec`), offered when the product has a
 * Color axis; packs stay colour-agnostic.
 *
 * Saving an edit keeps spec 13 honest, and which of three things happens is
 * stated in the dialog before you commit:
 *
 *  - composition unchanged → no library write at all (the usual "fix the
 *    price" edit shouldn't mint a row identical to its parent);
 *  - changed over the SAME scale labels → `storePacks.version` when the pack
 *    is referenced (the entry re-points; versioning re-points nothing by
 *    itself), `storePacks.edit` when it isn't;
 *  - changed to a DIFFERENT set of labels → a new pack. R3 fixes a family to
 *    its scale so `version` cannot re-scale, and R2 says a run that omits a
 *    size is a different size curve belonging on its own scale.
 *
 * State is local and committed only on Save; the dialog is mounted only while
 * open, so every open seeds fresh — which is why prefill lives in the
 * `useState` initialisers and reads only synchronous data off the entry.
 */
export function PricingEntryDialog({
  open,
  onOpenChange,
  siblings,
  editIndex,
  taxPct,
  variantAttributes,
  productVariants,
  coverageGuard,
  onSave,
}: PricingEntryDialogProps) {
  const editing = editIndex !== undefined ? siblings[editIndex] : undefined;

  // Only the edit path reads the library, and only to decide what a save does
  // to it (`packSaveAction`): the pack's scale labels say whether the new
  // composition is the same size curve, and its `referenced` flag whether that
  // means a new version or an in-place edit. Nothing here is displayed — the
  // builder prefills from the ENTRY, synchronously, so it never waits on this.
  // Adding a price composes a new pack and never browses (#247).
  const packs = trpc.storePacks.list.useQuery(undefined, { enabled: editing !== undefined });
  const scales = trpc.storePacks.listScales.useQuery(undefined, {
    enabled: editing !== undefined,
  });
  const utils = trpc.useUtils();
  const ensureScale = trpc.storePacks.ensureScale.useMutation();
  const createPack = trpc.storePacks.create.useMutation();
  const versionPack = trpc.storePacks.version.useMutation();
  const editPack = trpc.storePacks.edit.useMutation();

  const active = useMemo(
    () => (packs.data ?? []).filter((p) => p.status === "active"),
    [packs.data],
  );
  const scaleLabels = useMemo(
    () => new Map((scales.data ?? []).map((s) => [s.scaleId, s.labels])),
    [scales.data],
  );

  // ── the two axes the builder feeds on ───────────────────────────
  const sizeAxis = findAxis(variantAttributes, ["size"]);
  const colorAxis = findAxis(variantAttributes, ["color", "colour"]);
  const sizes = sizeAxis?.values ?? [];
  const colors = colorAxis?.values ?? [];
  const oversized = sizes.filter((s) => s.length > LABEL_MAX);
  // The builder needs sizes that can become scale labels (≤8 chars, ≤24).
  const buildable = sizes.length >= 1 && sizes.length <= SIZES_MAX && oversized.length === 0;

  // With generated variants, the builder composes over THEM — one stepper per
  // sellable combination — and the size-level pack composition plus the colour
  // spec derive from the picks. Bare sizes are the fallback for a product that
  // has a Size axis but hasn't generated variants yet.
  const generated = (productVariants ?? []).filter(
    (v) => Object.keys(v.attributeValues).length > 0,
  );

  /**
   * Editing composes in the SAME builder as adding, prefilled — but only at a
   * grain the entry can actually be replayed at. Variant grain needs every
   * `variantKey` the entry recorded to still resolve to a generated variant;
   * an axis renamed since, or an entry written before #235 carried no
   * `variantComposition` at all, and prefilling variant keys into size-keyed
   * rows (or the reverse) renders a silently all-zero builder.
   */
  const variantKeysNow = new Set(generated.map((v) => variantKey(v.attributeValues)));
  const editVariantQty =
    editing?.variantComposition && editing.variantComposition.length > 0
      ? editing.variantComposition.map((v) => ({ key: variantKey(v.attributeValues), qty: v.qty }))
      : null;
  const editMapsToVariants =
    editVariantQty !== null &&
    generated.length > 0 &&
    editVariantQty.every((v) => variantKeysNow.has(v.key));

  const variantMode = generated.length > 0 && (editing ? editMapsToVariants : buildable);

  /**
   * Row set: the product's variants when the entry replays at that grain, else
   * size rows — and on edit those are **the entry's own labels**, not the
   * product's Size axis. A pack routinely holds sizes the product's axis no
   * longer lists (it was authored against an older axis, or through the old
   * library picker): composing over today's axis would quietly drop the rest,
   * so opening an entry and saving only a price would shrink its size curve.
   * The entry's labels need no library round-trip either — R2 requires every
   * scale label to carry qty ≥ 1, so the serialised ratio IS the full set.
   * Adding has no entry to be faithful to, so it uses the product's sizes.
   */
  const entryPairs = editing ? compositionPairs(editing.sizeRatio) : [];
  const buildRows: CompositionRow[] = variantMode
    ? generated.map((v) => ({
        key: variantKey(v.attributeValues),
        label: Object.values(v.attributeValues).join(" · "),
        sublabel: v.sku || undefined,
      }))
    : editing && entryPairs.length > 0
      ? entryPairs.map((p) => ({ key: p.label, label: p.label }))
      : sizes.map((s) => ({ key: s, label: s }));

  /** Editable at all: adding needs a usable Size axis; editing can always fall
   *  back to the pack's own labels, so it only fails on an unparseable ratio. */
  const composable = editing ? buildRows.length > 0 : buildable;

  // ── shared state ────────────────────────────────────────────────
  const [price, setPrice] = useState(
    editing?.pricePaise && editing.pricePaise !== "0"
      ? (Number(editing.pricePaise) / 100).toString()
      : "",
  );
  const [discount, setDiscount] = useState(
    editing?.discountBps ? (editing.discountBps / 100).toString() : "",
  );
  const [moq, setMoq] = useState(editing?.moq?.toString() ?? "");
  const [packsOnHand, setPacksOnHand] = useState(editing?.packsOnHand?.toString() ?? "");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // ── colour overlay ──────────────────────────────────────────────
  const [colorMode, setColorMode] = useState<ColorMode>(editing?.colorSpec?.mode ?? "mixed");
  const [selectedColors, setSelectedColors] = useState<string[]>(editing?.colorSpec?.colors ?? []);

  // ── builder state ───────────────────────────────────────────────
  // Seeded in the initialiser, not an effect: the dialog is mounted only while
  // open, so every open seeds once from `editing` and the operator's edits are
  // never clobbered afterwards. Everything read here is synchronous — the
  // library queries below resolve too late for a `useState` initialiser, which
  // is why prefill comes off the ENTRY and never off `selected.composition`.
  const [bundleName, setBundleName] = useState(editing?.label ?? "");
  const [bundleQty, setBundleQty] = useState<Record<string, number>>(() => {
    if (!editing) return {};
    if (editMapsToVariants && editVariantQty) {
      return Object.fromEntries(editVariantQty.map((v) => [v.key, v.qty]));
    }
    return Object.fromEntries(compositionPairs(editing.sizeRatio).map((p) => [p.label, p.qty]));
  });

  // The entry's own pack. Absent when it has been archived out of `active`, or
  // simply not loaded yet — either way the builder above is already prefilled
  // from the entry, so this is only consulted at save time to decide between
  // versioning and editing in place (R3).
  const packId = editing?.packId ?? "";
  const selected = active.find((p) => p.packId === packId);
  const selectedScaleLabels = selected ? (scaleLabels.get(selected.scaleId) ?? []) : [];

  const pricePaise = rupeesToPaise(price);
  const discountBps = pctToBps(discount);
  const taxBps = pctToBps(taxPct);
  const discounted = (pricePaise * BigInt(10_000 - discountBps)) / 10_000n;

  const qtyOnHand = Number(packsOnHand);

  // Only the edit path queries; adding never waits on the library.
  const loading = editing !== undefined && (packs.isLoading || scales.isLoading);

  // In variant mode the colours DERIVE from which variants carry quantity —
  // a separate toggle could only agree or contradict, so it isn't shown.
  const derivedSelection =
    variantMode && sizeAxis
      ? bundleFromVariantSelection(
          generated,
          generated.map((v) => bundleQty[variantKey(v.attributeValues)] ?? 0),
          sizeAxis.name,
          colorAxis?.name ?? null,
          sizes,
        )
      : null;

  /**
   * Pieces in one pack, always the composition CURRENTLY in the builder — on
   * edit too, so the derived lines track what the operator is doing rather
   * than what the pack used to hold. Summing over `buildRows` (not `sizes`)
   * keeps it right for all three row sets; the two key spaces never mix.
   */
  const pieces =
    variantMode && derivedSelection
      ? derivedSelection.pieces
      : buildRows.reduce((total, r) => total + (bundleQty[r.key] ?? 0), 0);

  /**
   * The bundle in the builder right now, as the library models it — size-level
   * composition, its ordered scale labels, and the per-variant breakdown when
   * there is one. Unvalidated on purpose: the R3 notice has to describe what a
   * save WOULD do while the form is still half-filled, and `composeFromBuilder`
   * below is the single validating gate over this same value, so the two can't
   * drift.
   */
  const built =
    variantMode && derivedSelection
      ? {
          composition: derivedSelection.composition,
          // The scale carries only the sizes actually used — spec 13 R2's own
          // "a run that skips a size belongs on its own scale".
          scaleLabelList: derivedSelection.usedSizes,
          variantComposition: generated
            .map((v) => ({
              attributeValues: v.attributeValues,
              qty: bundleQty[variantKey(v.attributeValues)] ?? 0,
            }))
            .filter((v) => v.qty > 0),
        }
      : {
          composition: Object.fromEntries(buildRows.map((r) => [r.key, bundleQty[r.key] ?? 0])),
          scaleLabelList: buildRows.map((r) => r.key),
          variantComposition: undefined,
        };

  const builtSizeRatio = serialiseComposition(built.composition, built.scaleLabelList);

  /** The library answered. Distinct from `!selected`: a pack missing from a
   *  loaded list is archived or superseded, which is a fact; a pack missing
   *  because the request failed is ignorance, and must not drive a write. */
  const libraryLoaded = packs.isSuccess && scales.isSuccess;

  /**
   * What a save would do to the library **right now** — computed once and used
   * by all three consumers: the notice under the builder, the name-collision
   * guard, and `saveEdit` itself, so what the dialog promises is what the click
   * does (PR #260 review L2, where they could disagree).
   */
  const saveAction = editing
    ? packSaveAction({
        entrySizeRatio: editing.sizeRatio,
        entryLabel: editing.label,
        nextSizeRatio: builtSizeRatio,
        nextName: bundleName.trim(),
        nextScaleLabels: built.scaleLabelList,
        // Absent from a LOADED list ⇒ archived, or superseded by a newer
        // version of its family (`storePacks.list` returns only the latest).
        // Either way it can't be versioned, so a changed bundle mints instead.
        packScaleLabels: selected ? selectedScaleLabels : null,
        referenced: selected?.referenced ?? false,
      })
    : null;

  /**
   * Minting a new pack needs a free name, and the entry's CURRENT pack is
   * still holding the old one — so keeping the name is a guaranteed
   * `DuplicatePackNameError`. Say so here rather than letting the operator
   * discover it from a server round-trip after they've filled the form.
   */
  const nameTaken =
    saveAction?.kind === "create" && active.some((p) => p.name.trim() === bundleName.trim());

  const piecesOnHand =
    Number.isInteger(qtyOnHand) && qtyOnHand > 0
      ? entryPiecesOnHand({ packsOnHand: qtyOnHand, pieces })
      : 0;

  const effectiveColors = derivedSelection ? derivedSelection.colors : selectedColors;
  const effectiveMode: ColorMode = derivedSelection
    ? derivedSelection.colors.length > 1
      ? "mixed"
      : "single"
    : colorMode;

  const colorLine =
    effectiveColors.length === 0
      ? null
      : effectiveMode === "mixed"
        ? `Mixed colors · ${effectiveColors.join(", ")}`
        : effectiveColors[0] ?? null;

  /** The colour overlay to persist, or nothing when no colour is in play. */
  const colorSpec =
    effectiveColors.length > 0
      ? {
          mode: effectiveMode,
          colors: effectiveMode === "single" ? effectiveColors.slice(0, 1) : effectiveColors,
        }
      : undefined;

  /** Money checks shared by every path. Returns the parsed figures or null. */
  const validateMoney = (): { parsedMoq: number; parsedQty: number } | null => {
    if (pricePaise <= 0n) {
      setError("Enter a price for this pack.");
      return null;
    }
    const parsedMoq = Number(moq);
    if (moq.trim() && (!Number.isInteger(parsedMoq) || parsedMoq < 1)) {
      setError("MOQ must be a whole number.");
      return null;
    }
    if (!packsOnHand.trim()) {
      setError("Enter the quantity on hand (packs).");
      return null;
    }
    const parsedQty = Number(packsOnHand);
    if (!Number.isInteger(parsedQty) || parsedQty < 0) {
      setError("Quantity must be a whole number.");
      return null;
    }
    if (moq.trim() && parsedQty > 0 && parsedMoq > parsedQty) {
      setError(
        `MOQ can't exceed the quantity on hand (${parsedQty} packs) — buyers couldn't order this pack.`,
      );
      return null;
    }
    // Coverage against the opening stock (ADR-0065 companion) — refused HERE,
    // where the quantity is being committed, not later on a step change.
    const coverageMsg = coverageGuard?.(pieces * parsedQty, editIndex);
    if (coverageMsg) {
      setError(coverageMsg);
      return null;
    }
    return { parsedMoq, parsedQty };
  };

  const emit = (entry: {
    packId: string;
    label: string;
    sizeRatio: string;
    pieces: number;
    parsedMoq: number;
    parsedQty: number;
    variantComposition?: { attributeValues: Record<string, string>; qty: number }[] | undefined;
  }) => {
    onSave({
      kind: "pack",
      packId: entry.packId,
      label: entry.label,
      sizeRatio: entry.sizeRatio,
      pieces: entry.pieces,
      pricePaise: pricePaise.toString(),
      discountBps,
      ...(moq.trim() ? { moq: entry.parsedMoq } : {}),
      packsOnHand: entry.parsedQty,
      ...(colorSpec ? { colorSpec } : {}),
      ...(entry.variantComposition ? { variantComposition: entry.variantComposition } : {}),
    });
    onOpenChange(false);
  };

  /**
   * The single validating gate over `built`, shared by both save paths —
   * adding and editing compose identically now, and the only difference is what
   * happens to the pack afterwards. Sets `error` and returns false on failure,
   * so callers just bail.
   */
  const composeIsValid = (): boolean => {
    if (variantMode) {
      if (pieces === 0) {
        setError("Set a quantity on at least one variant.");
        return false;
      }
    } else {
      // Size grain: every row must carry a quantity (R2 completeness — a pack
      // covers every label of its scale). `buildRows` is the product's sizes
      // when adding, and can be the pack's own labels when editing an entry
      // the current axes can't express.
      const missing = buildRows.filter((r) => !((bundleQty[r.key] ?? 0) > 0)).map((r) => r.label);
      if (missing.length > 0) {
        setError(
          `Every size needs a quantity — ${missing.join(", ")} ${missing.length === 1 ? "is" : "are"} empty.`,
        );
        return false;
      }
    }
    if (pieces > 1000) {
      setError("A bundle holds at most 1000 pieces.");
      return false;
    }
    return true;
  };

  // ── save: edit an existing entry ────────────────────────────────
  const saveEdit = async () => {
    if (!editing) return;
    const name = bundleName.trim();
    if (!name) return setError("Give the bundle a name.");
    if (nameTaken) {
      return setError(
        `“${name}” is already a pack. Changing the sizes creates a new one, so give this bundle a different name.`,
      );
    }
    if (!composeIsValid()) return;
    const money = validateMoney();
    if (!money) return;

    // Never write to the library on an unanswered question. Without the list we
    // can't tell "this pack is gone" from "we couldn't ask", and the former
    // mints a pack while the latter must not.
    if (saveAction && saveAction.kind !== "reuse" && !libraryLoaded) {
      return setError("Couldn't load the pack library — reopen this entry and try again.");
    }

    const { composition, scaleLabelList, variantComposition } = built;
    const commit = (entryPackId: string) =>
      emit({
        packId: entryPackId,
        label: name,
        sizeRatio: builtSizeRatio,
        pieces,
        parsedMoq: money.parsedMoq,
        parsedQty: money.parsedQty,
        // Recomputed, never carried over — `emit` builds the entry fresh and
        // the list replaces it wholesale, so an omitted key is an ERASED key.
        ...(variantComposition ? { variantComposition } : {}),
      });

    // The same `saveAction` the notice and the name guard read, so what the
    // dialog promised before the click is what the click does. Spec 13 owns
    // the rules; `packSaveAction` holds them and the tests.
    const action = saveAction;
    if (!action || action.kind === "reuse") return commit(packId);

    setSaving(true);
    try {
      let entryPackId: string;
      if (action.kind === "version") {
        // R3: versioning re-points NOTHING by itself — the entry has to.
        const res = await versionPack.mutateAsync({ packId, name, composition });
        entryPackId = res.packId;
      } else if (action.kind === "edit") {
        const res = await editPack.mutateAsync({ packId, name, composition });
        entryPackId = res.packId;
      } else {
        // Different size curve ⇒ different scale ⇒ a new pack (R2). `version`
        // can't re-scale a family, so this is the only spec-legal route.
        const { scaleId } = await ensureScale.mutateAsync({ labels: scaleLabelList });
        const created = await createPack.mutateAsync({ name, scaleId, composition });
        entryPackId = created.packId;
      }
      await utils.storePacks.list.invalidate();
      await utils.storePacks.listScales.invalidate();
      setSaving(false);
      commit(entryPackId);
    } catch (err) {
      setSaving(false);
      setError(storeErrorMessage(err));
    }
  };

  // ── save: build new bundle ──────────────────────────────────────
  const saveBuild = async () => {
    const name = bundleName.trim();
    if (!name) return setError("Give the bundle a name.");
    if (!composeIsValid()) return;
    const money = validateMoney();
    if (!money) return;

    const { composition, scaleLabelList, variantComposition } = built;
    setSaving(true);
    try {
      // Find-or-create the scale for these sizes, then a real library pack —
      // inline authoring that still lands in the library (S-PK4's intent).
      const { scaleId } = await ensureScale.mutateAsync({ labels: scaleLabelList });
      const created = await createPack.mutateAsync({ name, scaleId, composition });
      await utils.storePacks.list.invalidate();
      await utils.storePacks.listScales.invalidate();
      setSaving(false);
      emit({
        packId: created.packId,
        label: name,
        sizeRatio: builtSizeRatio,
        pieces,
        parsedMoq: money.parsedMoq,
        parsedQty: money.parsedQty,
        ...(variantComposition ? { variantComposition } : {}),
      });
    } catch (err) {
      setSaving(false);
      setError(storeErrorMessage(err));
    }
  };

  const save = () => {
    setError(null);
    if (editing) void saveEdit();
    else void saveBuild();
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      {/* Capped + scrollable: build mode (steppers × sizes + preview + swatches)
          outgrows a laptop viewport, and a footer pushed below the fold is a
          Save button that cannot be clicked. Build mode also gets a wider
          dialog — the stepper list, preview panel and four money fields don't
          fit a 512px column (operator feedback 2026-08-08). */}
      <DialogContent size={composable ? "xl" : "lg"} className="max-h-[88dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.label}` : "Add bundle"}</DialogTitle>
          <DialogDescription>
            One bundle per way this product sells. Add another for a different composition or
            minimum.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-1">
          {loading ? (
            <Skeleton className="h-9 w-full" />
          ) : composable ? (
            <div className="flex flex-col gap-2">
              <BundleCompositionBuilder
                rows={buildRows}
                requireAll={!variantMode}
                name={bundleName}
                onNameChange={(n) => {
                  setBundleName(n);
                  setError(null);
                }}
                qty={bundleQty}
                onQtyChange={(q) => {
                  setBundleQty(q);
                  setError(null);
                }}
                colorLine={colorLine}
              />
              {/* R3, stated where the consequence is incurred. It used to live
                  inside the adjust panel; with the builder always open on edit
                  it has to say what a save will do — and stays silent on
                  `reuse`, or every price tweak would read as if it rewrote the
                  library. Driven by the same `saveAction` the Save button
                  obeys, so the promise and the behaviour cannot diverge. */}
              {saveAction && saveAction.kind !== "reuse" ? (
                <p className={`text-xs ${nameTaken ? "text-bad" : "text-muted"}`}>
                  {saveAction.kind === "create"
                    ? nameTaken
                      ? `Saving creates a NEW pack — and “${bundleName.trim()}” is already taken. Give this bundle a different name.`
                      : saveAction.reason === "pack-unavailable"
                        ? "This entry's pack is no longer the current one — it was archived, or another product moved the family to a newer version. It can't be changed, so saving creates a NEW pack and this product moves to it; existing orders are untouched."
                        : "These sizes differ from the pack's, so saving creates a NEW pack — a different size curve is a different pack. This product moves to it; existing orders keep the old one."
                    : saveAction.kind === "version"
                      ? "This pack is referenced, so saving creates a new version — this product moves to it; existing orders keep the old one."
                      : "This pack is unreferenced, so it updates in place."}
                </p>
              ) : null}
            </div>
          ) : (
            /* Nothing to compose, and no picker to fall back to (#247). The
               refusal has to name what's missing and where to fix it, or the
               operator is left with a dialog that only says no. */
            <div className="rounded-sm border border-dashed border-line px-4 py-6 text-center">
              <p className="text-sm text-ink-2">This product can't be priced yet.</p>
              <p className="mt-1 text-xs text-muted">
                {sizes.length === 0 ? (
                  <>
                    Every price is a bundle composed from the product's own sizes. Add a{" "}
                    <span className="font-medium text-ink-2">Size</span> attribute with values in
                    the Attributes section first.
                  </>
                ) : oversized.length > 0 ? (
                  <>
                    {oversized.join(", ")} {oversized.length === 1 ? "exceeds" : "exceed"} the{" "}
                    {LABEL_MAX}-character size-label limit, so{" "}
                    {oversized.length === 1 ? "it" : "they"} can't become a size scale. Shorten{" "}
                    {oversized.length === 1 ? "it" : "them"} in the Attributes section.
                  </>
                ) : (
                  /* Backstop, not a live path: the attribute schema already caps
                     values at 24 (`variant-form.ts` — `.max(24)`), so a Size axis
                     can't exceed SIZES_MAX through the form. `buildable` checks it
                     anyway, and a checked condition with no copy is a blank dialog. */
                  <>
                    This product has {sizes.length} sizes — a bundle composes at most {SIZES_MAX}.
                    Trim the Size attribute in the Attributes section.
                  </>
                )}
              </p>
            </div>
          )}

          {/* Colour overlay. Hidden in variant-composed builds: there the
              colours DERIVE from which variants carry quantity (shown in the
              preview's colour line), and a manual toggle could only agree or
              contradict. Manual picking remains on edit and in size-mode
              builds, where nothing else states the colours. */}
          {!loading && colors.length > 0 && !variantMode && composable ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium leading-[18px] text-ink-2">Colours</span>
              <ColorSpecPicker
                colors={colors}
                mode={colorMode}
                selected={selectedColors}
                onModeChange={setColorMode}
                onSelectedChange={setSelectedColors}
              />
            </div>
          ) : null}

          {/* Nothing to price when there's no composable bundle — the refusal
              above is the whole body then. */}
          {!loading && composable ? (
            <div className="flex flex-col gap-4 border-t border-line pt-4">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted">
                Pricing &amp; stock
              </span>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="entry-price"
                    className="text-sm font-medium leading-[18px] text-ink-2"
                  >
                    Price (₹ per pack) *
                  </label>
                  <Input
                    id="entry-price"
                    className="mt-auto"
                    value={price}
                    inputMode="decimal"
                    placeholder="5000.00"
                    onChange={(e) => {
                      setPrice(e.target.value);
                      setError(null);
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="entry-discount"
                    className="text-sm font-medium leading-[18px] text-ink-2"
                  >
                    Discount (%)
                  </label>
                  <Input
                    id="entry-discount"
                    className="mt-auto"
                    value={discount}
                    inputMode="decimal"
                    placeholder="0"
                    onChange={(e) => {
                      setDiscount(e.target.value);
                      setError(null);
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="entry-moq"
                    className="text-sm font-medium leading-[18px] text-ink-2"
                  >
                    MOQ (packs)
                  </label>
                  <Input
                    id="entry-moq"
                    className="mt-auto"
                    value={moq}
                    inputMode="numeric"
                    placeholder="10"
                    onChange={(e) => {
                      setMoq(e.target.value);
                      setError(null);
                    }}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="entry-packs-on-hand"
                    className="text-sm font-medium leading-[18px] text-ink-2"
                  >
                    Quantity (packs) *
                  </label>
                  <Input
                    id="entry-packs-on-hand"
                    className="mt-auto"
                    value={packsOnHand}
                    inputMode="numeric"
                    placeholder="10"
                    onChange={(e) => {
                      setPacksOnHand(e.target.value);
                      setError(null);
                    }}
                  />
                </div>
              </div>

              {pricePaise > 0n ? (
                <p className="text-xs text-muted" aria-hidden>
                  {pieces > 0 ? (
                    <>
                      ≈{" "}
                      <span className="font-mono tabular-nums text-ink-2">
                        {paiseToInrExact(perPiecePaise(pricePaise, pieces).toString())}
                      </span>
                      /pc · {pieces} pcs ·{" "}
                    </>
                  ) : null}
                  {discountBps > 0 ? `−${discountBps / 100}% → ` : ""}
                  <span className="font-mono font-semibold tabular-nums text-ink">
                    {paiseToInrExact(withGstPaise(discounted, taxBps).toString())}
                  </span>{" "}
                  {taxBps > 0 ? `incl. ${taxBps / 100}% GST` : "no GST"}
                </p>
              ) : null}

              {piecesOnHand > 0 ? (
                <p className="text-xs text-muted" aria-hidden>
                  <span className="font-mono tabular-nums text-ink-2">{qtyOnHand}</span> packs ×{" "}
                  <span className="font-mono tabular-nums text-ink-2">{pieces}</span> pcs ={" "}
                  <span className="font-mono font-semibold tabular-nums text-ink">
                    {piecesOnHand.toLocaleString("en-IN")}
                  </span>{" "}
                  pcs on hand
                </p>
              ) : null}

              {error ? <p className="text-xs text-bad">{error}</p> : null}
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button intent="ghost" size="md" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            intent="primary"
            size="md"
            disabled={loading || saving || !composable}
            onClick={save}
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Create bundle & price"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
